import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { useFormContext } from "react-hook-form";
import { logError } from "repoV2/utils/common/error/error";
import { useCheckoutBasicDetailsContext } from "repoV2/features/CheckoutPages/modules/CheckoutBasicDetailsContextProvider";
import { IListing } from "repoV2/features/Listing/Listing.interfaces";
import { checkIsObjectEmpty } from "repoV2/utils/common/dataTypes/object";
import { CHECKOUT_PAGES_ACTION_TYPES } from "repoV2/features/CheckoutPages/constants/CheckoutPagesReducer";
import { callGetUpsellListingsApi } from "repoV2/features/Listing/utils/callGetUpsellListingsApi";
import { IRepoV2ReduxState } from "repoV2/features/Common/modules/Reducer/Reducer.interface";
import { UPSELL_TYPES } from "repoV2/features/Listing/Listing.constants";
import {
    default_listing_quantity,
    order_bumps_form_key,
} from "./OrderBumpsFormGroup.constants";
import { IOrderBumpsFormValues } from "./OrderBumpsFormGroup.interfaces";
import {
    useCouponCodeFormGroup,
    useRemoveCouponCode,
} from "../CouponCodeFormGroup/CouponCodeFormGroup.utils";
import { COUPON_TYPES } from "../CouponCodeFormGroup/CouponCodeFormGroup.constants";

export const useOrderBumpsFormGroup = () => {
    const { setValue, watch } = useFormContext();
    const selectedOrderBumps: IOrderBumpsFormValues =
        watch(order_bumps_form_key);

    const [isOrderBumpsLoading, setIsOrderBumpsLoading] = React.useState(true);
    const { onApplyCouponCodeClick, isCouponCodeApplied, appliedCouponType } =
        useCouponCodeFormGroup();
    const { onRemoveCouponCodeClick } = useRemoveCouponCode();

    const dispatch = useDispatch();

    const {
        listingUuid,
        customOrderBumpsHeading,
        enableRedirectionOnOrderBumps,
        dynamicLinkUuid,
        isCheckoutPagePreviewMode,
    } = useCheckoutBasicDetailsContext();

    const orderBumpsListing: IListing.IUpsellListing[] =
        useSelector(
            (state: IRepoV2ReduxState) =>
                state.checkoutPages.listingDetails?.[listingUuid]
                    ?.orderBumpsDetails?.data?.orderBumpsList
        ) || [];

    const orderBumpsFormGroupsHeading =
        customOrderBumpsHeading || "Bought Together";

    // This is the useEffect used to update the selected order bumps in react hook form while in preview mode,
    // when the user makes changes in data in the CT side for checkout order bumps
    React.useEffect(() => {
        // - isCheckoutPagePreviewMode - We check if the checkout page is being opened in preview mode, meaning creator is
        // using the Edit checkout page flow on CT
        // - dynamicLinkUuid - Check if it is a custom checkout page
        // - Array.isArray(orderBumpsListing) && !isOrderBumpsLoading - We have to make sure that the order bumps api has been loaded
        // and data is stored in the 'orderBumpsListing' state even if its empty array. Other wise this useEffect loads more than once on mount
        // and the setValue(order_bumps_form_key, updatedOrderBumpsFormData) causes error when updating the same state.
        //
        // So we have to make sure that this useEffect is only running once after we have fetched the data, and then whenever the 'orderBumpsListing'
        // data is changed, which can only happen when user makes changes on Order Bumps Section on CT flow of Edit Checkout Page
        if (
            isCheckoutPagePreviewMode &&
            dynamicLinkUuid &&
            Array.isArray(orderBumpsListing) &&
            !isOrderBumpsLoading
        ) {
            const updatedOrderBumpsFormData: IOrderBumpsFormValues = {};
            orderBumpsListing.forEach(orderBumpValue => {
                if (orderBumpValue.allow_order_bump_in_purchase) {
                    updatedOrderBumpsFormData[
                        orderBumpValue.additional_listing_uid
                    ] = {
                        listingDetails: {
                            ...orderBumpValue,
                        },
                        selectedQuantity: default_listing_quantity,
                    };
                }
            });
            setValue(order_bumps_form_key, updatedOrderBumpsFormData);
        }
    }, [orderBumpsListing, isOrderBumpsLoading]); // orderBumpsListing is fetched client side

    const getOrderBumpsListings = async () => {
        try {
            const response = await callGetUpsellListingsApi({
                listingUuid,
                upsellType: UPSELL_TYPES.ORDER_BUMPS,
                dynamicLinkUuid,
            });

            const orderBumpsResponse: IListing.IUpsellListing[] =
                response.data?.data?.upsell_listings;
            const initialOrderBumpsFormData: IOrderBumpsFormValues = {};
            orderBumpsResponse.forEach(orderBumpValue => {
                if (orderBumpValue.allow_order_bump_in_purchase) {
                    initialOrderBumpsFormData[
                        orderBumpValue.additional_listing_uid
                    ] = {
                        listingDetails: {
                            ...orderBumpValue,
                        },
                        selectedQuantity: default_listing_quantity,
                    };
                }
            });
            dispatch({
                type: CHECKOUT_PAGES_ACTION_TYPES.SET_ORDER_BUMPS_DATA,
                payload: {
                    listingUuid,
                    data: {
                        orderBumpsCount: orderBumpsResponse.length,
                        orderBumpsList: orderBumpsResponse,
                    },
                },
            });
            setValue(order_bumps_form_key, initialOrderBumpsFormData);
        } catch (err) {
            logError({
                error: err,
                occuredAt:
                    "repoV2/features/CheckoutPages/modules/PaymentDetailsSection/modules/FormGroups/modules/OrderBumpsFormGroup/OrderBumpsFormGroup.utils.ts",
                when: "Order Bumps fetch api",
            });
        } finally {
            setIsOrderBumpsLoading(false);
        }
    };

    React.useEffect(() => {
        getOrderBumpsListings();
    }, []);

    const handleOrderBumpsSelection = ({
        checked,
        currentListing,
        newListingQuantity,
    }: {
        checked: boolean;
        currentListing: IListing.IUpsellListing;
        newListingQuantity: number;
    }) => {
        const selectedUuid = currentListing.additional_listing_uid;
        if (checked) {
            setValue(order_bumps_form_key, {
                ...selectedOrderBumps,
                [selectedUuid]: {
                    listingDetails: {
                        ...currentListing,
                    },
                    selectedQuantity: newListingQuantity || 1,
                },
            });
            // Incase user makes a change in order bumps, then we need to remove
            // the coupon that has already been applied, as some coupons, those available for both the main and the
            // order bump listing can not be applied, and this is being checked at server, so user will re-apply the coupon
            // if it is not valid and we'll remove it, else we will not remove it if its is valid
            if (isCouponCodeApplied && checkIsObjectEmpty(selectedOrderBumps)) {
                // Here, 'selectedOrderBumps' represent the order bumps that were already selected
                // before the execution of this function, so if there is nothing in it, this means that
                // originally selected order bumps were empty, and we have selected an order bump
                onApplyCouponCodeClick({
                    ...(appliedCouponType === COUPON_TYPES.LOYALTY_COUPON && {
                        couponType: appliedCouponType,
                    }),
                    onError: () => {
                        onRemoveCouponCodeClick();
                    },
                });
            }
        } else {
            const newSelectedListings = { ...selectedOrderBumps };
            delete newSelectedListings[selectedUuid];
            setValue(order_bumps_form_key, {
                ...newSelectedListings,
            });
        }
    };

    return {
        orderBumpsListing,
        orderBumpsFormGroupsHeading,
        enableRedirectionOnOrderBumps,
        handleOrderBumpsSelection,
        selectedOrderBumps,
    };
};
